import React, { useState } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { Gift, Star, Zap, Calendar, Users, ArrowRight, Check, Copy, ExternalLink, MessageCircle } from 'lucide-react';

const AirdropPage = () => {
  const { connected, publicKey } = useWallet();
  const [copied, setCopied] = useState(false);

  const contractAddress = "HmjCoarLh5duURfJ333DwfFiPyTCgFT35pRSAoP8pump";
  const websiteURL = "https://tokenos.ai";
  const twitterHandle = "@TokenOS_AI";

  const handleCopyAddress = async () => {
    if (publicKey) {
      try {
        await navigator.clipboard.writeText(publicKey.toString());
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy address:', err);
      }
    }
  };

  const handleCopyTemplate = async () => {
    const template = `🚀 Just discovered @TokenOS_AI - the AI-powered platform that turns your ideas into deployed web3 projects in minutes!

✨ Features I love:
• Natural language → Full applications
• One-click token launching
• Instant deployment & hosting
• Built for Solana ecosystem

Check it out: https://tokenos.ai
CA: HmjCoarLh5duURfJ333DwfFiPyTCgFT35pRSAoP8pump
My wallet: [YOUR_WALLET_ADDRESS]

#TokenOS #Web3 #AI #Solana #BuildDifferent

💡 Customize this template with your own experience and make it authentic!`;

    try {
      await navigator.clipboard.writeText(template);
      alert('Template copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy template:', err);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pt-16">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="text-6xl mb-6">🪂</div>
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="text-white">$TOS Token</span>
            <span className="bg-gradient-to-r from-purple-500 to-violet-500 bg-clip-text text-transparent"> Airdrop</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Earn free $TOS tokens by promoting TokenOS on X.com!
          </p>
        </div>

        {/* How to Earn */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">How to Earn Free $TOS Tokens</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6 text-center">
              <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Create a Post on X.com</h3>
              <p className="text-gray-300 text-sm">
                Write a post that promotes TokenOS and its features
              </p>
            </div>
            
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6 text-center">
              <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Include Required Elements</h3>
              <p className="text-gray-300 text-sm">
                Your post must include all required elements (see checklist below)
              </p>
            </div>
            
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6 text-center">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Post & Wait</h3>
              <p className="text-gray-300 text-sm">
                Publish your post and wait for the airdrop distribution
              </p>
            </div>
          </div>
        </div>

        {/* Required Elements Checklist */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-8">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
              <Check className="h-6 w-6 text-green-400 mr-3" />
              Required Elements Checklist
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Check className="h-5 w-5 text-green-400 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-white">Include Website Link</h3>
                  <p className="text-gray-300 text-sm">Include {websiteURL} or your referral link</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <Check className="h-5 w-5 text-green-400 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-white">Tag TokenOS</h3>
                  <p className="text-gray-300 text-sm">Tag {twitterHandle} in your post</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <Check className="h-5 w-5 text-green-400 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-white">Include Contract Address</h3>
                  <p className="text-gray-300 text-sm font-mono text-xs">Include the CA: {contractAddress}</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <Check className="h-5 w-5 text-green-400 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-white">Include Your Wallet Address</h3>
                  <p className="text-gray-300 text-sm">Include your Solana wallet address</p>
                  {connected && publicKey && (
                    <div className="mt-2 p-2 bg-black/20 rounded flex items-center justify-between">
                      <span className="font-mono text-xs text-purple-300">
                        {publicKey.toString().slice(0, 8)}...{publicKey.toString().slice(-8)}
                      </span>
                      <button
                        onClick={handleCopyAddress}
                        className="text-gray-400 hover:text-white transition-colors"
                      >
                        {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <Check className="h-5 w-5 text-green-400 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-white">Promote TokenOS Features</h3>
                  <p className="text-gray-300 text-sm">Promote TokenOS features or benefits authentically</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-purple-900/50 to-violet-900/50 rounded-xl border border-purple-500/30 p-8">
            <h2 className="text-2xl font-bold text-white mb-6">Sample Post Template</h2>
            
            <div className="bg-black/20 rounded-lg p-4 mb-6">
              <p className="text-gray-300 text-sm leading-relaxed">
                🚀 Just discovered @TokenOS_AI - the AI-powered platform that turns your ideas into deployed web3 projects in minutes!
                <br/><br/>
                ✨ Features I love:<br/>
                • Natural language → Full applications<br/>
                • One-click token launching<br/>
                • Instant deployment & hosting<br/>
                • Built for Solana ecosystem<br/><br/>
                Check it out: https://tokenos.ai<br/>
                CA: HmjCoarLh5duURfJ333DwfFiPyTCgFT35pRSAoP8pump<br/>
                My wallet: [YOUR_WALLET_ADDRESS]<br/><br/>
                #TokenOS #Web3 #AI #Solana #BuildDifferent<br/><br/>
                💡 Customize this template with your own experience and make it authentic!
              </p>
            </div>
            
            <button
              onClick={handleCopyTemplate}
              className="w-full px-4 py-3 bg-gradient-to-r from-purple-500 to-violet-500 text-white rounded-lg hover:opacity-90 transition-opacity"
            >
              📋 Copy Template
            </button>
          </div>
        </div>

        {/* Token Information */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">$TOS Token Details</h2>
          
          <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-gray-700">
                  <span className="text-gray-400">Token Name</span>
                  <span className="text-white font-medium">TokenOS</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-700">
                  <span className="text-gray-400">Symbol</span>
                  <span className="text-white font-medium">$TOS</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-700">
                  <span className="text-gray-400">Network</span>
                  <span className="text-white font-medium">Solana</span>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-gray-700">
                  <span className="text-gray-400">Website</span>
                  <a href={websiteURL} target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 transition-colors">
                    {websiteURL}
                  </a>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-700">
                  <span className="text-gray-400">Twitter</span>
                  <a href={`https://x.com/TokenOS_AI`} target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 transition-colors">
                    {twitterHandle}
                  </a>
                </div>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-purple-900/30 border border-purple-500/30 rounded-lg">
              <p className="text-sm text-purple-300 font-medium mb-2">Contract Address:</p>
              <p className="text-xs font-mono text-purple-200 break-all">
                {contractAddress}
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-purple-900/50 to-violet-900/50 rounded-xl border border-purple-500/30 p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Ready to Participate?</h2>
          <p className="text-gray-300 mb-6">
            Create your promotional post on X.com and earn free $TOS tokens!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://x.com/intent/tweet"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-gradient-to-r from-purple-500 to-violet-500 text-white rounded-lg hover:opacity-90 transition-opacity inline-flex items-center justify-center"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Create X Post
            </a>
            <a
              href="https://t.me/tokenos_ai"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors inline-flex items-center justify-center"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Join Community
            </a>
          </div>
          
          <p className="text-gray-400 text-sm mt-6">
            Make sure to follow all requirements and create authentic content to be eligible for the airdrop
          </p>
        </div>
      </div>
    </div>
  );
};

export default AirdropPage;